import React, {ReactNode, useEffect, useLayoutEffect, useRef, useState} from 'react';
import {Button} from '~/components/Button';
import style from './index.module.scss';

type FooterAlign = 'start' | 'end';

type ToggleVisibility = 'auto' | 'always' | 'never';

const copyToClipboard = async (text: string): Promise<boolean> => {
    if (!text) return false;

    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
        try {
            await navigator.clipboard.writeText(text);
            return true;
        } catch {}
    }

    try {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        textarea.style.top = '0';
        document.body.appendChild(textarea);
        textarea.select();
        const ok = document.execCommand('copy');
        textarea.remove();
        return ok;
    } catch {
        return false;
    }
};

interface ICollapsibleSectionProps {
    children: ReactNode;
    collapsedHeight?: number;
    defaultExpanded?: boolean;
    expandLabel?: ReactNode;
    collapseLabel?: ReactNode;
    canCopy?: boolean;
    copyText?: string;
    copyLabel?: ReactNode;
    copiedLabel?: ReactNode;
    footerActions?: ReactNode;
    footerAlign?: FooterAlign;
    toggle?: ToggleVisibility;
    className?: string;
    contentClassName?: string;
    footerClassName?: string;
}

export const CollapsibleSection = ({
    children,
    collapsedHeight = 220,
    defaultExpanded = false,
    expandLabel = 'Expand',
    collapseLabel = 'Hide',
    canCopy = false,
    copyText,
    copyLabel = 'Copy',
    copiedLabel = 'Copied',
    footerActions,
    footerAlign = 'end',
    toggle = 'auto',
    className,
    contentClassName,
    footerClassName,
}: ICollapsibleSectionProps) => {
    const [expanded, setExpanded] = useState(defaultExpanded);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const [copied, setCopied] = useState(false);
    const contentRef = useRef<HTMLDivElement | null>(null);
    const copyTimeoutRef = useRef<number | null>(null);

    const updateOverflow = () => {
        const node = contentRef.current;
        if (!node || expanded) return;

        const next = node.scrollHeight > node.clientHeight + 1;
        setIsOverflowing(prev => (prev === next ? prev : next));
    };

    useLayoutEffect(() => {
        if (expanded) {
            setIsOverflowing(false);
            return;
        }
        updateOverflow();
    });

    useEffect(() => {
        if (expanded) return;
        window.addEventListener('resize', updateOverflow);
        return () => window.removeEventListener('resize', updateOverflow);
    }, [expanded]);

    useEffect(() => {
        return () => {
            if (copyTimeoutRef.current !== null) {
                window.clearTimeout(copyTimeoutRef.current);
            }
        };
    }, []);

    const showToggle =
        toggle === 'always' || (toggle === 'auto' && (expanded || isOverflowing));
    const showCopy = canCopy;
    const showFooter = Boolean(footerActions) || showToggle || showCopy;

    const contentClasses = [
        style.content,
        !expanded && style.contentCollapsed,
        contentClassName,
    ]
        .filter(Boolean)
        .join(' ');

    const footerClasses = [
        style.footer,
        footerAlign === 'end' ? style.footerEnd : style.footerStart,
        footerClassName,
    ]
        .filter(Boolean)
        .join(' ');

    const handleCopy = async () => {
        const node = contentRef.current;
        const text = typeof copyText === 'string' ? copyText : (node?.textContent ?? '');
        const ok = await copyToClipboard(text);
        if (!ok) return;

        setCopied(true);
        if (copyTimeoutRef.current !== null) {
            window.clearTimeout(copyTimeoutRef.current);
        }
        copyTimeoutRef.current = window.setTimeout(() => setCopied(false), 800);
    };

    return (
        <div className={className}>
            <div
                ref={contentRef}
                className={contentClasses}
                style={expanded ? undefined : {maxHeight: collapsedHeight}}
            >
                {children}
                {!expanded && isOverflowing && <div className={style.fade} aria-hidden />}
            </div>
            {showFooter && (
                <div className={footerClasses}>
                    {showToggle && toggle !== 'never' && (
                        <Button small onClick={() => setExpanded(prev => !prev)}>
                            {expanded ? collapseLabel : expandLabel}
                        </Button>
                    )}
                    {showCopy && (
                        <Button
                            small
                            leftIcon={<i className={copied ? 'fa fa-check' : 'far fa-copy'}/>}
                            onClick={handleCopy}
                        >
                            {copied ? copiedLabel : copyLabel}
                        </Button>
                    )}
                    {footerActions}
                </div>
            )}
        </div>
    );
};
